// InvitePage.jsx — /spaces/:spaceId/invite
// v1.9 — Revert bulk-entry pill to "Invite several people at once" (v1.8 wrongly
//   set "Send Invite"); drop the → arrows from both top pills. Presentation only.
// v1.8 — Warm invite polish (presentation only): pill corners squared to match
//   the Send button; extra space above the form; Cancel -> terracotta-outline (mockup).
// v1.7 — NEW-41: say WHY Send is disabled. Leaving the name blank greyed the
//   button out silently, which is indistinguishable from a broken button — it
//   briefly read as a bug while verifying NEW-13. BulkInvitePage already explains
//   the same condition, so this reuses its exact wording and .inlineHint styling
//   rather than inventing a second phrasing. Shown only once an email has been
//   entered, so an untouched form is not nagged at.
//   Same defect class as NEW-11 / NEW-18: a blocked action with no explanation.
// v1.6 — NEW-13: warn before creating a duplicate contributor row.
//   This form posted the invite blind and only learned of a clash from a
//   server ALREADY_INVITED error afterwards - and duplicates exist in prod,
//   so that check is not catching the already-ACTIVE case. It now re-fetches
//   GET /spaces/:id/contributors (dropped in v1.1 with the inline list) purely
//   to build the lookup, and disables Send while the typed address is already
//   active or pending here. Revoked addresses stay invitable.
//   Lookup + copy come from utils/contributorStatus.js, shared with
//   BulkInvitePage, so the two screens cannot drift apart on this question.
//   NOTE: this is a courtesy check only. The server remains the authority and
//   its guard should be verified separately.
// v1.5 — B3.3: top actions styled as buttons (not text links); order = bulk-invite
//   first, then manage contributors. (July 26, 2026)
// v1.4 — B3.1: secondary links moved to a top row (right-aligned); Cancel added
//   below Send (returns to feed). Relocation ships to everyone; bulk link stays
//   flag-gated. (July 26, 2026)
// v1.3 — B3: additive flag-gated "Invite several people at once" link to the
//   bulk-invite page (rendered only when appState.flags.group_spaces_enabled).
//   Single-invite behavior/payload byte-unchanged. (July 26, 2026)
// v1.2 — UI polish: header consistency with Write page (April 29, 2026)
//
// Changes from v1.1:
//   - Replaced inline header with shared LovedOneBar component
//     (circular avatar with space photo, consistent with Write/Record pages)
//   - Subtitle shows "Invite someone"
//
// v1.1 — Invite UX cleanup. (April 17, 2026)
//
// Changes from v1.0:
//   - REMOVED: Inline "Invited (N)" contributor list JSX + associated
//     `contributors` state + `GET /spaces/:id/contributors` fetch on mount
//     + post-send list refresh. Reason: the list does not scale past a few
//     rows (20, 200 contributors make the page unusable). The canonical
//     contributor management view lives in Settings.
//   - ADDED: "Manage contributors →" text link below Send invite button
//     inside the form card. Deep-links to
//     /settings?from={spaceId}&section=contributors per SettingsPage.jsx
//     URL contract (searchParams `from` + `section`).
//   - CHANGED: Success feedback moved from inline .successBanner to a
//     transient bottom-center toast overlay. Auto-dismisses after 3s
//     (Red Hat / Material Design 3 guidance for transient success toasts).
//     `role="status"` + `aria-live="polite"` so screen readers announce it.
//     Dismiss timer is cleared on unmount and on re-send (prevents
//     setState-after-unmount and stacked timers).
//
// Intentionally UNTOUCHED from v1.0:
//   - POST /spaces/:spaceId/invite request payload and error handling
//     (ALREADY_INVITED → specific message, else generic)
//   - Header + form fields (name, email, personal note) + Send button
//   - Privacy note at the bottom
//   - Loading state
//
// APIs: POST /spaces/:id/invite
// (v1.0 called GET /spaces/:id/contributors — removed in v1.1)

import { useState, useEffect, useCallback, useRef } from 'react';
import { buildStatusByEmail, isAlreadyInSpace, statusNoticeFor, lookupStatus } from '../utils/contributorStatus';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppContext } from '../App';
import { createApiClient } from '../api/client';
import styles from './InvitePage.module.css';
import LovedOneBar from '../components/LovedOneBar';
import { useResolvedPhotoUrl } from '../hooks/useResolvedPhotoUrl';

// v1.1: Toast auto-dismiss duration — Red Hat / Material Design 3 guidance
// for transient success confirmations.
const TOAST_MS = 3000;

export default function InvitePage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const appState = useAppContext();
  // B3: gate the bulk-invite entry (dark unless group_spaces_enabled).
  const groupSpacesEnabled = appState?.flags?.group_spaces_enabled === true;

  const [space, setSpace] = useState(null);
  // v1.2: Resolve S3 key to signed CloudFront URL for LovedOneBar avatar.
  const resolvedPhotoUrl = useResolvedPhotoUrl(space, getAccessTokenSilently);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null); // v1.1: { text } | null
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // NEW-13: lowercased email -> strongest existing contributor status here.
  const [statusByEmail, setStatusByEmail] = useState(() => new Map());

  // v1.1: Hold the toast auto-dismiss timer so we can cancel it on unmount
  // or when a new toast replaces an existing one.
  const toastTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const api = createApiClient(getAccessTokenSilently);
        // v1.1: Only load the space. Contributor fetch removed — the list
        // is no longer rendered on this page.
        const spaceData = await api.get(`/spaces/${spaceId}`);
        // NEW-13: lookup only - no list is rendered (removed in v1.1).
        // Tolerates failure: a missing lookup degrades to the old behaviour
        // (server-side ALREADY_INVITED) rather than blocking the invite.
        try {
          const contribData = await api.get(`/spaces/${spaceId}/contributors`);
          setStatusByEmail(buildStatusByEmail(contribData?.contributors || []));
        } catch (contribErr) {
          console.error('Invite: contributor lookup failed', contribErr);
        }
        if (cancelled) return;
        setSpace(spaceData);
      } catch (err) {
        console.error('Invite load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, getAccessTokenSilently]);

  // v1.1: Clear toast timer on unmount.
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, []);

  // v1.1: Shared helper — set a toast that auto-dismisses. Cancels any
  // prior pending dismiss so back-to-back sends don't stack timers.
  const showToast = useCallback((text) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast({ text });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, TOAST_MS);
  }, []);

  const handleSend = useCallback(async () => {
    if (!email.trim() || !name.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const api = createApiClient(getAccessTokenSilently);
      await api.post(`/spaces/${spaceId}/invite`, {
        email: email.trim(),
        contributorName: name.trim(),
        message: message.trim() || null,
      });
      // v1.1: Toast overlay replaces inline success banner.
      showToast(`Invite sent to ${email.trim()}`);
      setEmail('');
      setName('');
      setMessage('');
      // v1.1: No post-send contributor refresh — list removed.
    } catch (err) {
      if (err.error === 'ALREADY_INVITED') {
        setError('This person has already been invited.');
      } else {
        setError("Couldn't send invite. Please try again.");
      }
    } finally {
      setSending(false);
    }
  }, [email, name, message, sending, spaceId, getAccessTokenSilently, showToast]);

  // v1.1: Deep-link to the Contributors panel in Settings. Confirmed URL
  // contract by inspecting SettingsPage.jsx (reads `from` + `section` via
  // useSearchParams and resolveInitialSection).
  const handleManageContributors = useCallback(() => {
    navigate(`/settings?from=${spaceId}&section=contributors`);
  }, [navigate, spaceId]);

  // NEW-13: derived from the typed address on every render.
  const existingStatus = lookupStatus(statusByEmail, email);
  const alreadyInSpace = isAlreadyInSpace(existingStatus);
  const alreadyNotice = statusNoticeFor(existingStatus);

  // NEW-41: only once they have started — an untouched form should stay quiet.
  const missingName = !name.trim() && !!email.trim();

  const spaceInitial = space?.name ? space.name.charAt(0).toUpperCase() : '?';

  if (loading) {
    return <div className={styles.loading}><div className={styles.loadingDot} /><span>Loading...</span></div>;
  }

  return (
    <div className={styles.screen}>
      {/* v1.2: Shared LovedOneBar — consistent with Write/Record pages */}
      <LovedOneBar
        spaceName={space?.name}
        spacePhotoUrl={resolvedPhotoUrl}
        subtitle="Invite someone"
        onBack={() => navigate(`/spaces/${spaceId}`)}
      />

      <div className={styles.content}>
        {/* B3.1: secondary links moved to a top row, right-aligned (page body). */}
        <div className={styles.topActions}>
          {groupSpacesEnabled && (
            <button type="button" className={styles.manageLink} onClick={() => navigate(`/spaces/${spaceId}/invite/bulk`)}>
              Invite several people at once
            </button>
          )}
          <button type="button" className={styles.manageLink} onClick={handleManageContributors}>
            Manage contributors
          </button>
        </div>

        {/* Invite form */}
        <div className={styles.formCard}>
          <h3 className={styles.formTitle}>Invite a family member or friend</h3>
          <p className={styles.formDesc}>They'll be able to add their own memories to {space?.name}'s space.</p>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Their name</label>
            <input
              type="text"
              className={styles.fieldInput}
              placeholder="e.g., Aunt Sarah"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Their email</label>
            <input
              type="email"
              className={styles.fieldInput}
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-describedby={alreadyInSpace ? 'invite-already-notice' : undefined}
            />
            {alreadyInSpace && (
              <p id="invite-already-notice" className={styles.fieldNotice} role="status">
                {alreadyNotice}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Personal note <span className={styles.optional}>(optional)</span></label>
            <textarea
              className={styles.fieldTextarea}
              placeholder={`e.g., I've been keeping some memories of ${space?.name || 'them'} here...`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>

          {/* v1.1: Inline success banner removed. Success surfaces as a
              bottom-center toast overlay (rendered at screen-level below). */}
          {missingName && (
            <div className={styles.inlineHint}>Please add their name, so we can greet them in the invitation.</div>
          )}
          {error && <div className={styles.errorBanner}>{error}</div>}

          <button
            className={styles.btnPrimary}
            onClick={handleSend}
            disabled={!email.trim() || !name.trim() || sending || alreadyInSpace}
          >
            {sending ? 'Sending...' : 'Send invite'}
          </button>

          {/* B3.1: Cancel below Send — returns to the feed. */}
          <button type="button" className={styles.cancelLink} onClick={() => navigate(`/spaces/${spaceId}`)}>
            Cancel
          </button>
        </div>

        {/* Privacy note — unchanged from v1.0 */}
        <div className={styles.privacyNote}>
          <span>🔒</span>
          <span>Contributors only see shared memories. They can never see your private memories.</span>
        </div>
      </div>

      {/* v1.1: Bottom-center success toast — auto-dismisses after TOAST_MS.
          Rendered at screen level so it overlays the content rather than
          displacing form layout. */}
      {toast && (
        <div
          className={styles.toastOverlay}
          role="status"
          aria-live="polite"
        >
          <div className={styles.toast}>
            <span className={styles.toastIcon} aria-hidden="true">✓</span>
            <span>{toast.text}</span>
          </div>
        </div>
      )}
    </div>
  );
}
